import { marked } from 'marked';

/**
 * Wrap the rendered markdown in a self-contained HTML document with
 * baseline typography, so the S3-hosted artifact page renders decently
 * without any external CSS. Used by the "Share" action on Artifacts
 * pages + inside ConversationPageTemplate.
 */
export const renderArtifactHtml = (markdown: string, title = 'Artifact'): string => {
  const body = marked.parse(markdown ?? '', {
    async: false,
    gfm: true,
    breaks: true,
  }) as string;

  const escape = (s: string): string =>
    s.replace(/[&<>"']/g, (c) =>
      c === '&'
        ? '&amp;'
        : c === '<'
          ? '&lt;'
          : c === '>'
            ? '&gt;'
            : c === '"'
              ? '&quot;'
              : '&#39;',
    );

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escape(title)}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@100;200;300;400;500;600;700&display=swap"
      rel="stylesheet"
    />
    <style>
      :root { color-scheme: light dark; }
      body {
        margin: 0;
        padding: 32px 16px;
        font-family: "IBM Plex Sans Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
          Helvetica, Arial, sans-serif;
        background: #f8fafc;
        color: #0f172a;
        line-height: 1.6;
      }
      main {
        max-width: 720px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 16px;
        padding: 32px 40px;
        box-shadow: 0 4px 20px -12px rgba(0, 0, 0, 0.15);
      }
      h1, h2, h3, h4, h5, h6 { line-height: 1.2; margin-top: 1.6em; }
      h1 { font-size: 1.75rem; }
      h2 { font-size: 1.35rem; }
      h3 { font-size: 1.15rem; }
      p { margin: 0.75em 0; }
      ul, ol { padding-left: 1.5em; }
      pre {
        background: #0f172a;
        color: #e2e8f0;
        padding: 12px 16px;
        border-radius: 8px;
        overflow-x: auto;
      }
      code {
        background: #f1f5f9;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.9em;
      }
      pre code { background: transparent; padding: 0; }
      a { color: #2563eb; }
      blockquote {
        border-left: 4px solid #cbd5f5;
        margin: 1em 0;
        padding: 0.5em 1em;
        color: #475569;
        background: #f8fafc;
        border-radius: 0 8px 8px 0;
      }
      hr { border: 0; border-top: 1px solid #e2e8f0; margin: 2em 0; }
      table { border-collapse: collapse; margin: 1em 0; }
      th, td { border: 1px solid #e2e8f0; padding: 6px 12px; }
      @media (prefers-color-scheme: dark) {
        body { background: #0f172a; color: #e2e8f0; }
        main { background: #1e293b; box-shadow: none; }
        code { background: #334155; color: #f1f5f9; }
        blockquote { background: #1e293b; color: #cbd5f5; border-left-color: #334155; }
        hr { border-top-color: #334155; }
        th, td { border-color: #334155; }
      }
    </style>
  </head>
  <body>
    <main>
      ${body}
    </main>
  </body>
</html>`;
};
